import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { getCredentials, hasCredentials } from '../config/credentials'

/**
 * Centralized Axios client for OCAPI Data API with OAuth authentication.
 */

const BASE_URL = '/mobify/proxy/ocapi/s/-/dw/data/v25_6' as const
const OAUTH_URL = '/mobify/proxy/oauth/dwsso/oauth2/access_token' as const
const TIMEOUT_MS = 10_000 as const

/**
 * Token cache to avoid unnecessary token requests.
 */
let cachedToken: string | null = null
let tokenExpiry: number = 0

/**
 * Get OAuth access token using client credentials grant.
 */
async function getAccessToken(): Promise<string> {
    // Return cached token if still valid (with 60s buffer)
    if (cachedToken && Date.now() < tokenExpiry - 60000) return cachedToken

    if (!hasCredentials()) throw new Error('SFCC credentials not configured')

    const { clientId, clientSecret } = getCredentials()

    const params = new URLSearchParams()
    params.append('grant_type', 'client_credentials')
    params.append('client_id', clientId)
    params.append('client_secret', clientSecret)

    const response = await axios.post<{ access_token: string; expires_in: number }>(
        OAUTH_URL,
        params.toString(),
        {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            timeout: TIMEOUT_MS
        }
    )

    cachedToken = response.data.access_token
    tokenExpiry = Date.now() + response.data.expires_in * 1000

    return cachedToken
}

export const apiClient = axios.create({
    baseURL: BASE_URL,
    timeout: TIMEOUT_MS,
    headers: {
        'Content-Type': 'application/json'
    }
}) satisfies AxiosInstance

/**
 * Request interceptor: attach Bearer token for authentication.
 */
apiClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        const token = await getAccessToken()
        config.headers.Authorization = `Bearer ${token}`
        return config
    },
    (error: unknown) => {
        return Promise.reject(error)
    }
)

/**
 * Response interceptor: handle 401 errors by clearing token cache.
 */
apiClient.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
            cachedToken = null
            tokenExpiry = 0
        }
        return Promise.reject(error)
    }
)

export default apiClient
